// utils/cropImage.ts
import { loadDrawable, toBlobAsync } from './imageCanvas';

export const __testables = {
  createCanvas: (): HTMLCanvasElement => document.createElement('canvas'),
};

export async function getCroppedImageBlob(
  imageSrc: string,
  crop: { x: number; y: number; width: number; height: number },
  opts?: {
    size?: number; // output width when outputHeight is not set (square), default 768
    outputWidth?: number; // explicit output width
    outputHeight?: number; // explicit output height (enables non-square output)
    quality?: number; // 0..1 (for webp/jpeg), default 0.82
    forceType?: 'image/jpeg' | 'image/webp' | 'image/png'; // optional
  }
): Promise<Blob> {
  const outW = opts?.outputWidth ?? opts?.size ?? 768;
  const outH = opts?.outputHeight ?? opts?.size ?? 768;
  const quality = opts?.quality ?? 0.82;

  // Use ImageBitmap when available to honor EXIF rotation (iOS photos, etc.)
  const drawable = await loadDrawable(imageSrc);

  const canvas = __testables.createCanvas();
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');

  if (!ctx) throw new Error('2D context not available');

  // High-quality downscale
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    drawable,
    crop.x, crop.y, crop.width, crop.height,
    0, 0, outW, outH
  );

  // Prefer WebP → JPEG → PNG (or honor a forced type)
  const types: ('image/webp' | 'image/jpeg' | 'image/png')[] =
    opts?.forceType ? [opts.forceType] : ['image/webp', 'image/jpeg', 'image/png'];

  for (const type of types) {
    const blob = await toBlobAsync(canvas, type, quality);
    if (blob) return blob; // use the first supported encoding
  }

  throw new Error('Failed to encode canvas to Blob');
}
